// Typed API client for user content system

import { communityService } from '../../services/CommunityService';
import { recipeService } from '../../services/RecipeService';
import { routineService } from '../../services/RoutineService';
import { workoutService } from '../../services/WorkoutService';
import { UserContentType, UserContentItem, UserContentCounts } from './types';
import { AccessDecisionReason, AccessMode, AccessState, AccessVisibility } from '../../models/content';
import { DateTime } from 'luxon';

export const USER_CONTENT_PAGE_SIZE = 20;

export interface FetchUserContentOptions {
  skip?: number;
  afterDate?: number;
}

const fallbackAccess = {
  accessState: AccessState.Viewable,
  reason: AccessDecisionReason.Free,
  visibility: AccessVisibility.Public,
  accessMode: AccessMode.Free,
  unlockOptions: {
    subscribe: false,
    purchase: false,
    adUnlock: false,
  },
};

export async function fetchUserContent(
  type: UserContentType,
  userId: string,
  isOwner = false,
  options: FetchUserContentOptions = {},
): Promise<UserContentItem[]> {
  const skip = options.skip ?? 0;
  const afterDate = options.afterDate ?? DateTime.utc().toMillis();

  switch (type) {
    case 'post': {
      const posts = await communityService.fetchUserPosts(userId, isOwner, afterDate, skip);
      return posts.map((post) => ({
        id: post._id!,
        type: 'post',
        title: post.title,
        summary: post.content?.slice(0, 120),
        imageUrl: post.headerImage,
        dateCreated: post.dateCreated,
        dateModified: post.dateModified,
        access: fallbackAccess,
        accessModel: post.accessModel,
        data: post,
      }));
    }
    case 'recipe': {
      const recipes = await recipeService.fetchUserRecipes(userId, afterDate, skip);
      return recipes.map((item) => {
        const recipe = item.recipe;
        const preview = item.preview;
        return {
          id: preview?.entityId ?? String(recipe?.id ?? ''),
          type: 'recipe' as const,
          title: recipe?.title ?? preview?.displayName ?? 'Recipe',
          summary: recipe?.summary?.slice(0, 120) ?? preview?.subtitle,
          imageUrl: recipe?.image ?? preview?.imageUrl,
          dateCreated: recipe?.dateCreated ?? 0,
          dateModified: recipe?.dateModified,
          access: item.access,
          accessModel: recipe?.accessModel,
          data: recipe,
          preview,
        };
      });
    }
    case 'workout': {
      const workouts = await workoutService.fetchUserWorkouts(userId, isOwner, afterDate, skip);
      return workouts.map((workout) => ({
        id: String((workout as any)._id ?? (workout as any).sqlId ?? workout.id),
        type: 'workout',
        title: workout.name,
        summary: workout.description?.slice(0, 120),
        imageUrl: undefined, // Add image if available in model
        dateCreated: workout.dateCreated,
        dateModified: workout.dateModified,
        access: fallbackAccess,
        accessModel: workout.accessModel,
        data: workout,
      }));
    }
    case 'routine': {
      const routines = await routineService.fetchAuthorRoutines(userId, isOwner, afterDate, skip);
      return routines.map((routine) => ({
        id: routine._id!,
        type: 'routine',
        title: routine.title,
        summary: routine.content?.slice(0, 120),
        imageUrl: routine.headerImage,
        dateCreated: routine.dateCreated,
        dateModified: routine.dateModified,
        access: fallbackAccess,
        accessModel: routine.accessModel,
        data: routine,
      }));
    }
    default:
      return [];
  }
}

export async function fetchUserContentCounts(userId: string, isOwner = false): Promise<UserContentCounts> {
  const countType = async (type: UserContentType): Promise<number> => {
    let total = 0;
    let skip = 0;

    while (true) {
      const batch = await fetchUserContent(type, userId, isOwner, { skip });
      total += batch.length;

      if (batch.length < USER_CONTENT_PAGE_SIZE) {
        break;
      }

      skip += batch.length;
    }

    return total;
  };

  const [posts, recipes, workouts, routines] = await Promise.all([
    countType('post'),
    countType('recipe'),
    countType('workout'),
    countType('routine'),
  ]);

  return {
    post: posts,
    recipe: recipes,
    workout: workouts,
    routine: routines,
  };
}
